/*
 * Copyright (c) 2020. American Express Company
 */

// first-party
import * as _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { RuleModel } from 'src/types/rule.model';
import CategoryService from '../../../../services/category.service';
import IncomeSourceService from '../../../../services/income-source.service';
import RuleService from '../../../../services/rule.service';

// components
import Dropdown from '../../../menus/Dropdown';
import TabButton from 'src/bw/tab-button/TabButton';
import { ButtonTertiary, IconTrash, Input } from '@americanexpress/dls-react';
import Typography from '../../../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import WideView from 'src/components/atomic/layouts/wideView/WideView';

// icons and styles
import './UnifiedRulesDrawer.scss';
import { MenuItemModel, SASVariant } from 'src/types/view-models/search-and-select.view.model';
import SearchAndSelect from '../../../search-and-select/SearchAndSelect';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import bgColors from 'src/styles/bgColors.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import { isKeyboardKey } from 'src/utils/key.utils';
import { Key } from 'src/types/keys.enum';
export default class UnifiedRulesDrawer extends Component<any> {
    public override state = {
        changed: false,
        ruleSet: 'Expense rules',
        ruleChangedUid: null as string,
        incomeRuleChangedUid: null as string,
        showNewIncomeSource: false,
        newCategory: null as string,
        showNewCategory: false,
        newCategoryParentName: null as string,
        newCategoryParentId: null as string
    };

    private saveRuleTimer!: number;
    private saveIncomeRuleTimer!: number;

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;

        const userRules = _.filter(drawerProps.rules, (rule: RuleModel) => {
            return rule.transactionName;
        });

        const menuItemModels: MenuItemModel[] = SearchAndSelect.getMenuItemModels(
            _.sortBy(drawerProps.categories, 'name')
        );

        const addCategoryDiv =
            this.state.showNewCategory || this.state.showNewIncomeSource ? (
                <div className="new-category-dialog" key="new-category-dialog">
                    <Typography
                        variant={TypographyVariants.HEADING_M}
                        key="new-category-text"
                        className="new-category-text after16"
                    >
                        {this.state.showNewCategory
                            ? `Add new category to ${this.state.newCategoryParentName}`
                            : 'Add new income source'}
                    </Typography>
                    <Input
                        key="new-category"
                        id="new-category"
                        type="string"
                        className="new-category after16"
                        value={this.state.newCategory}
                        variant="outlined"
                        onChange={(e: any) => {
                            this.setState({ newCategory: e.target.value });
                        }}
                    />
                    <div className="buttons">
                        <ButtonTertiary
                            id="save"
                            key="save-category"
                            onClick={async () => {
                                await this.newCategory();
                            }}
                        >
                            {this.state.showNewCategory ? 'Add subcategory' : 'Add income source'}
                        </ButtonTertiary>
                        <ButtonTertiary
                            borderless={true}
                            id="save"
                            key="cancel-category"
                            onClick={() => {
                                this.setState({
                                    showNewCategory: false,
                                    showNewIncomeSource: false
                                });
                            }}
                        >
                            Cancel
                        </ButtonTertiary>
                    </div>
                </div>
            ) : null;

        const rules =
            this.state.ruleSet === 'Expense rules' ? (
                <React.Fragment>
                    {userRules?.length ? (
                        <div className="rules-table">
                            {userRules.map((rule: RuleModel) => {
                                const category: CategoryModel = _.find(drawerProps.categories, { id: rule.groupUid });
                                return (
                                    <div key={'rule' + rule.id} className="rule-row">
                                        <Typography key="text1" variant={TypographyVariants.SUBTEXT}>
                                            If name
                                        </Typography>
                                        <Dropdown
                                            value={rule.matcher}
                                            onChange={async (e: any) => {
                                                await this.changeRule(rule, 'matcher', e.target.value);
                                            }}
                                            className="group-select"
                                        >
                                            <option value="contains">contains</option>
                                            <option value="startsWith">starts with</option>
                                            <option value="endsWith">ends with</option>
                                        </Dropdown>
                                        <Input
                                            key="pattern"
                                            fullWidth
                                            type="string"
                                            value={rule.transactionName}
                                            variant="outlined"
                                            onChange={async (e: any) => {
                                                await this.changeRule(rule, 'transactionName', e.target.value);
                                            }}
                                            onKeyUp={async (e: any) => await this.handleKeyUpRule(rule, e)}
                                        />
                                        <Typography key="text2" variant={TypographyVariants.SUBTEXT}>
                                            {' '}
                                            move to{' '}
                                        </Typography>
                                        <SearchAndSelect
                                            name={category ? category.name : 'Category'}
                                            title="Category"
                                            className="group-select"
                                            items={menuItemModels}
                                            variant={SASVariant.ROUNDED}
                                            expanded={category ? [category.parentId] : []}
                                            selected={category ? category.id : null}
                                            onClick={async (item: MenuItemModel) => {
                                                await this.changeRule(rule, 'groupUid', item.value);
                                            }}
                                            addCallback={this.addCategory.bind(this)}
                                            addName="Add custom subcategory"
                                        />
                                        <ClickableIcon
                                            icon={<IconTrash />}
                                            onClick={() => {
                                                this.handleDeleteRule(rule).catch(console.error);
                                            }}
                                            className="delete"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-rules">
                            <Typography
                                key="no-rules-text"
                                variant={TypographyVariants.HEADING_S}
                                className="no-rules-text"
                            >
                                You have no expense rules
                            </Typography>
                        </div>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    {drawerProps.incomeRules?.length ? (
                        <div className="rules-table">
                            {drawerProps.incomeRules.map((rule: IncomeSourceRuleModel) => {
                                const incomeSource: IncomeSourceModel = _.find(drawerProps.incomeSources, {
                                    incomeUid: rule.incomeUid
                                });
                                if (!incomeSource) {
                                    return null;
                                }

                                return (
                                    <div key={'rule' + rule.incomeRuleUid} className="rule-row">
                                        <Typography key="text1" variant={TypographyVariants.SUBTEXT}>
                                            If name
                                        </Typography>
                                        <Dropdown
                                            value={rule.matcher}
                                            onChange={async (e: any) => {
                                                await this.changeIncomeRule(rule, 'matcher', e.target.value);
                                            }}
                                            className="group-select"
                                        >
                                            <option value="contains">contains</option>
                                            <option value="startsWith">starts with</option>
                                            <option value="endsWith">ends with</option>
                                        </Dropdown>
                                        <Input
                                            key="pattern"
                                            fullWidth
                                            type="string"
                                            value={rule.transactionNamePattern}
                                            variant="outlined"
                                            onChange={async (e: any) => {
                                                await this.changeIncomeRule(
                                                    rule,
                                                    'transactionNamePattern',
                                                    e.target.value
                                                );
                                            }}
                                            onKeyUp={async (e: any) => await this.handleKeyUpIncomeRule(rule, e)}
                                        />
                                        <Typography key="text2" variant={TypographyVariants.SUBTEXT}>
                                            {' '}
                                            move to{' '}
                                        </Typography>
                                        <Dropdown
                                            value={incomeSource.incomeUid}
                                            onChange={async (e: any) => {
                                                if (Number(e.target.value) === -1) {
                                                    this.setState({
                                                        showNewIncomeSource: true
                                                    });
                                                } else {
                                                    await this.changeIncomeRule(rule, 'incomeUid', e.target.value);
                                                }
                                            }}
                                            className="group-select"
                                        >
                                            return
                                            {drawerProps.incomeSources.map((source: any) => {
                                                return (
                                                    <option
                                                        key={`income-source-${source.incomeUid}`}
                                                        value={source.incomeUid}
                                                    >
                                                        {source.name}
                                                    </option>
                                                );
                                            })}
                                            <option value={-1}>New Income Source</option>
                                        </Dropdown>
                                        <ClickableIcon
                                            icon={<IconTrash />}
                                            onClick={() => {
                                                this.props.handleDeleteIncomeRule(rule);
                                            }}
                                            className="delete"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-rules">
                            <Typography
                                key="no-rules-text"
                                variant={TypographyVariants.HEADING_S}
                                className="no-rules-text"
                            >
                                You have no income rules
                            </Typography>
                        </div>
                    )}
                </React.Fragment>
            );

        return (
            <WideView
                classNameView="unified-rules-drawer"
                classNameContainer={bgColors.backgroundLight}
                showFooter
                titleElement={
                    <Typography
                        id="transactionsRules"
                        key="title"
                        className="title"
                        variant={TypographyVariants.HEADING_M}
                    >
                        {LiteralsService.getLiteral('UnifiedRulesDrawer.title')}
                    </Typography>
                }
                history={this.props.drawerProps.history}
            >
                <Typography key="subtitle" className="subtitle" variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('UnifiedRulesDrawer.subtitle')}
                </Typography>

                <TabButton
                    className="tab-button before16 after16"
                    labels={['Income rules', 'Expense rules']}
                    current={this.state.ruleSet}
                    onClickLeft={() => this.setState({ ruleSet: 'Income rules' })}
                    onClickRight={() => this.setState({ ruleSet: 'Expense rules' })}
                />

                {rules}
                {addCategoryDiv}
            </WideView>
        );
    }

    private async changeRule(rule: RuleModel, field: string, value: string): Promise<void> {
        (rule as any)[field] = value;

        if (this.state.ruleChangedUid && this.state.ruleChangedUid !== rule.id) {
            this.saveRule(this.state.ruleChangedUid).catch(console.error); // no await
        }

        this.setState({
            changed: true,
            ruleChangedUid: rule.id
        });

        if (this.saveRuleTimer) {
            clearTimeout(this.saveRuleTimer);
        }
        this.saveRuleTimer = window.setTimeout(() => {
            this.saveRule(rule.id).catch(console.error);
        }, 750);

        const rules: RuleModel[] = this.props.drawerProps.rules;
        const index = _.findIndex(rules, { id: rule.id });
        rules.splice(index, 1, rule);

        this.props.updateDrawer({
            ...this.props.drawerProps,
            rules
        });
    }

    private async handleDeleteRule(rule: RuleModel): Promise<void> {
        await this.props.handleDeleteRule(rule);
        this.setState({
            changed: true
        });
    }

    private async changeIncomeRule(rule: IncomeSourceRuleModel, field: string, value: string): Promise<void> {
        (rule as any)[field] = value;

        if (this.state.incomeRuleChangedUid && this.state.incomeRuleChangedUid !== rule.incomeUid) {
            this.saveIncomeRule(this.state.incomeRuleChangedUid).catch(console.error); // no await
        }

        this.setState({
            changed: true,
            incomeRuleChangedUid: rule.incomeRuleUid
        });

        if (this.saveIncomeRuleTimer) {
            clearTimeout(this.saveRuleTimer);
        }
        this.saveIncomeRuleTimer = window.setTimeout(() => {
            this.saveIncomeRule(rule.incomeUid).catch(console.error);
        }, 750);

        const rules: IncomeSourceRuleModel[] = this.props.drawerProps.rules;
        const index = _.findIndex(rules, { incomeRuleUid: rule.incomeRuleUid });
        rules.splice(index, 1, rule);

        this.props.updateDrawer({
            ...this.props.drawerProps,
            rules
        });
    }

    private async handleKeyUpRule(rule: RuleModel, event: any): Promise<void> {
        if (isKeyboardKey(event, Key.ENTER)) {
            await this.saveRule(rule.id);
            this.setState({ incomeRuleChangedUid: null });
            if (this.saveRuleTimer) {
                clearTimeout(this.saveRuleTimer);
            }
        }
    }

    private async handleKeyUpIncomeRule(rule: IncomeSourceRuleModel, event: any): Promise<void> {
        if (isKeyboardKey(event, Key.ENTER)) {
            await this.saveIncomeRule(rule.incomeUid);
            this.setState({ incomeRuleChangedUid: null });
            if (this.saveIncomeRuleTimer) {
                clearTimeout(this.saveIncomeRuleTimer);
            }
        }
    }

    private async saveRule(id: string): Promise<void> {
        const rule = _.find(this.props.drawerProps.rules, { id });
        if (rule) {
            await RuleService.updateRule(rule);
        }

        if (this.saveRuleTimer) {
            clearTimeout(this.saveRuleTimer);
        }
    }

    private async saveIncomeRule(id: string): Promise<void> {
        const rule = _.find(this.props.drawerProps.incomeRules, { incomeUid: id });
        if (rule) {
            await this.props.handleUpdateIncomeRule(rule);
        }

        if (this.saveIncomeRuleTimer) {
            clearTimeout(this.saveIncomeRuleTimer);
        }
    }

    private async newCategory(): Promise<void> {
        if (this.state.showNewCategory) {
            await CategoryService.createCategory({
                name: this.state.newCategory,
                parentId: this.state.newCategoryParentId
            } as CategoryModel);

            const categories: CategoryModel[] = await CategoryService.getCategories();

            this.props.updateDrawer({
                ...this.props.drawerProps,
                categories
            });
        } else {
            const newIncome: IncomeSourceModel = new IncomeSourceModel();
            newIncome.name = this.state.newCategory;
            await IncomeSourceService.createIncomeSource(newIncome);

            const incomeSources: IncomeSourceModel[] = _.sortBy(
                await IncomeSourceService.getIncomeSources(),
                (i) => i.name
            );

            this.props.updateDrawer({
                ...this.props.drawerProps,
                incomeSources
            });
        }

        this.setState({
            newCategory: '',
            showNewCategory: false,
            showNewIncomeSource: false
        });
    }

    private addCategory(parent: MenuItemModel): void {
        this.setState({
            showNewCategory: true,
            newCategoryParentName: parent.label,
            newCategoryParentId: parent.value
        });
    }
}
